import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Typography,
  Stack
} from '@mui/material';
import { Identifier, useDataProvider, useNotify, useRefresh, useListContext } from 'react-admin';

interface BulkPaymentStatusButtonsProps {
  selectedIds: Identifier[];
  apiBasePath?: string;
}

export const BulkPaymentStatusButtons: React.FC<BulkPaymentStatusButtonsProps> = ({
  selectedIds,
  apiBasePath = 'payments'
}) => {
  const [completeOpen, setCompleteOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [memo, setMemo] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { data } = useListContext();

  const handleBulkUpdate = async (status: number, dialogSetter: (open: boolean) => void) => {
    if (selectedIds.length === 0) {
      notify('更新する項目を選択してください', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const updateData: { paymentStatus: number; memo: string } = {
        paymentStatus: status,
        memo: memo.trim()
      };

      // 複数の項目を個別に更新
      const promises = selectedIds.map((id) => {
        const record = data.find((r) => r.id === id);
        return dataProvider.update(`${apiBasePath}/details`, {
          id,
          data: updateData,
          previousData: record
        });
      });

      await Promise.all(promises);

      const statusLabel = status === 3 ? '振込済み' : 'エラー';
      notify(`${selectedIds.length}件を${statusLabel}に更新しました`, { type: 'success' });
      dialogSetter(false);
      setMemo('');
      refresh();
    } catch (error) {
      notify('ステータスの更新に失敗しました', { type: 'error' });
      console.error('Failed to bulk update payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderDialog = (open: boolean, setOpen: (open: boolean) => void, status: number, title: string) => (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          選択された{selectedIds.length}件を{status === 3 ? '振込済み' : 'エラー'}に変更します。
        </Typography>

        <MuiTextField
          fullWidth
          label="メモ（任意）"
          multiline
          rows={4}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="変更理由や備考を入力してください"
          InputProps={{
            sx: {
              p: 2
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={() => handleBulkUpdate(status, setOpen)}
          variant="contained"
          disabled={loading || selectedIds.length === 0}
        >
          {loading ? '更新中...' : '更新'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        <Button
          variant="text"
          onClick={() => setCompleteOpen(true)}
          disabled={selectedIds.length === 0 || loading}
          size="small"
        >
          振込済み
        </Button>
        <Button
          variant="text"
          onClick={() => setErrorOpen(true)}
          disabled={selectedIds.length === 0 || loading}
          size="small"
        >
          エラー
        </Button>
      </Stack>

      {renderDialog(completeOpen, setCompleteOpen, 3, '一括振込済み更新')}
      {renderDialog(errorOpen, setErrorOpen, 99, '一括エラー更新')}
    </>
  );
};

export default BulkPaymentStatusButtons;
